import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  IGateway,
  IMetricasGateways,
  IMetricasGateway,
  IResumenDatosBase,
  IResumenGatewayStats,
} from 'modelos/src';
import * as Highcharts from 'highcharts';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import { GatewayService } from '../../../../../../auxiliares/servicios/http/gateways.service';
import {
  IHeatmapChartData,
  IScatterChartData,
} from '../../../../../../auxiliares/graficos/graficos.component';
import { GatewayMetricasService } from '../../servicios/gateway-metricas.service';
import { ChartColors } from '../../../../../../auxiliares/servicios/chart-colors.service';
import { ResumenesDatosService } from '../../../../../../auxiliares/servicios/http/resumenes-datos.service';

// Inicializar módulo de heatmap
HighchartsHeatmap(Highcharts);

/**
 * Pestaña Métricas del detalle de gateway: los gráficos que antes iban
 * debajo de la info. Las métricas de 15 días y el airtime de 24 h los pide
 * la página (también arman el resumen de Info); acá se piden solo los
 * uplinks de 6 h y el historial de envío de stats.
 */
@Component({
  selector: 'app-detalles-gateway-metricas',
  templateUrl: './detalles-metricas.component.html',
  styleUrl: './detalles-metricas.component.scss',
  standalone: false,
})
export class DetallesGatewayMetricasComponent implements OnChanges {
  private service = inject(GatewayService);
  private gatewayMetricasService = inject(GatewayMetricasService);
  private resumenesService = inject(ResumenesDatosService);

  @Input() gateway?: IGateway;
  @Input() metricas?: IMetricasGateways;
  @Input() airtime?: IMetricasGateway[];

  // Graficos Highcharts
  public chartPaquetesRecibidos: Highcharts.Options;
  public chartPaquetesTransmitidos: Highcharts.Options;
  public chartHeatmapRxFreqData: Highcharts.Options;
  public chartHeatmapTxFreqData: Highcharts.Options;
  public chartHeatmapRxDrData: Highcharts.Options;
  public chartHeatmapTxDrData: Highcharts.Options;
  public chartTxStatus: Highcharts.Options;
  public chartAirtimeHeatmap: Highcharts.Options;
  public chartScatterUplinks: Highcharts.Options;
  public chartHistorialStats: Highcharts.Options;

  private idPedido?: string;
  /** Hasta tenerlos, el gráfico de RSSI no dice "sin uplinks". */
  public uplinksCargados = false;

  private generarChartPaquetesRecibidos() {
    const res = this.gatewayMetricasService.procesarDatosRxPackets(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipLineChart();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categorias,
      },
      yAxis: {
        title: { text: 'Cantidad de paquetes' },
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          name: 'Paquetes Recibidos',
          type: 'line',
          data: res.datos[0].data,
          color: ChartColors.success(),
        },
      ],
      ...tooltip,
    };
    this.chartPaquetesRecibidos = options;
  }
  private generarChartPaquetesTransmitidos() {
    const res = this.gatewayMetricasService.procesarDatosTxPackets(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipLineChart();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categorias,
      },
      yAxis: {
        title: { text: 'Cantidad de paquetes' },
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          name: 'Paquetes Transmitidos',
          type: 'line',
          data: res.datos[0].data,
          color: ChartColors.info(),
        },
      ],
      ...tooltip,
    };
    this.chartPaquetesTransmitidos = options;
  }
  private generarChartHeatmapRxFreqData() {
    const res = this.gatewayMetricasService.procesarRxPacketsPerFreq(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipHeatmap();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categoriasX,
      },
      yAxis: {
        title: { text: 'Frecuencia' },
        categories: res.categoriasY,
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          name: res.heatmapData.name || 'Cantidad',
          type: 'heatmap',
          data: res.heatmapData.data,
        },
      ],
      ...tooltip,
    };
    this.chartHeatmapRxFreqData = options;
  }
  private generarChartHeatmapTxFreqData() {
    const res = this.gatewayMetricasService.procesarTxPacketsPerFreq(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipHeatmap();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categoriasX,
      },
      yAxis: {
        title: { text: 'Frecuencia' },
        categories: res.categoriasY,
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          name: res.heatmapData.name || 'Cantidad',
          type: 'heatmap',
          data: res.heatmapData.data,
        },
      ],
      ...tooltip,
    };
    this.chartHeatmapTxFreqData = options;
  }
  private generarChartHeatmapRxDrData() {
    const res = this.gatewayMetricasService.procesarRxPacketsPerDr(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipHeatmap();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categoriasX,
        labels: {
          rotation: -45,
        },
      },
      yAxis: {
        title: { text: 'Data Rate' },
        categories: res.categoriasY,
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          name: res.heatmapData.name || 'Cantidad',
          type: 'heatmap',
          data: res.heatmapData.data,
          dataLabels: {
            enabled: false,
          },
        },
      ],
      ...tooltip,
    };
    this.chartHeatmapRxDrData = options;
  }
  private generarChartHeatmapTxDrData() {
    const res = this.gatewayMetricasService.procesarTxPacketsPerDr(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipHeatmap();
    const options: Highcharts.Options = {
      xAxis: {
        categories: res.categoriasX,
        labels: {
          rotation: -45,
        },
      },
      yAxis: {
        title: { text: 'Data Rate' },
        categories: res.categoriasY,
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          name: res.heatmapData.name || 'Cantidad',
          type: 'heatmap',
          data: res.heatmapData.data,
          dataLabels: {
            enabled: false,
          },
        },
      ],
      ...tooltip,
    };
    this.chartHeatmapTxDrData = options;
  }
  private generarChartTxStatus() {
    const res = this.gatewayMetricasService.procesarTxPacketsPerStatus(
      this.metricas,
    );
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipTxStatus();
    const options: Highcharts.Options = {
      chart: {
        type: 'column',
      },
      xAxis: {
        categories: res.categorias,
        labels: {
          rotation: -45,
        },
      },
      yAxis: {
        title: { text: 'Cantidad de paquetes' },
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
      series: res.series.map((s) => ({
        type: 'column',
        name: s.name,
        data: s.data,
        color: s.color,
      })),
      ...tooltip,
    };
    this.chartTxStatus = options;
  }
  private generarChartAirtimeHeatmap(resultado: {
    heatmapData: IHeatmapChartData;
    categoriasX: string[];
    categoriasY: string[];
  }) {
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipAirtime();
    const options: Highcharts.Options = {
      xAxis: {
        categories: resultado.categoriasX,
        title: { text: 'Hora' },
        labels: {
          rotation: -45,
        },
      },
      yAxis: {
        title: { text: 'Canal' },
        categories: resultado.categoriasY,
        reversed: false,
      },
      colorAxis: {
        min: 0,
        minColor: '#e6f3ff',
        maxColor: '#0066cc',
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: [
        {
          name: resultado.heatmapData.name || 'Time-on-Air (ms)',
          type: 'heatmap',
          data: resultado.heatmapData.data,
          dataLabels: {
            enabled: false,
          },
        },
      ],
      ...tooltip,
    };
    this.chartAirtimeHeatmap = options;
  }
  private generarChartScatterUplinks(resultado: {
    scatterData: IScatterChartData[];
    colorAxisMin: number;
    colorAxisMax: number;
    colorAxisStops: [number, string][];
    minRssi: number;
    maxRssi: number;
  }) {
    const tooltip = this.gatewayMetricasService.getOpcionesTooltipUplinks();
    const options: Highcharts.Options = {
      chart: {
        type: 'scatter',
      },
      xAxis: {
        type: 'datetime',
      },
      yAxis: {
        title: { text: 'RSSI (dBm)' },
        min: resultado.minRssi,
        max: resultado.maxRssi,
      },
      colorAxis: {
        min: resultado.colorAxisMin,
        max: resultado.colorAxisMax,
        stops: resultado.colorAxisStops,
      },
      legend: {
        enabled: true,
        align: 'right',
        layout: 'vertical',
        verticalAlign: 'middle',
      },
      series: resultado.scatterData.map((s) => ({
        type: 'scatter',
        name: s.name,
        data: s.data,
        marker: {
          radius: 4,
        },
      })),
      ...tooltip,
    };
    this.chartScatterUplinks = options;
  }

  private generarGraficos(): void {
    if (!this.metricas) return;
    this.generarChartPaquetesRecibidos();
    this.generarChartPaquetesTransmitidos();
    this.generarChartHeatmapRxFreqData();
    this.generarChartHeatmapTxFreqData();
    this.generarChartHeatmapRxDrData();
    this.generarChartHeatmapTxDrData();
    this.generarChartTxStatus();
  }

  private generarGraficoAirtime(): void {
    if (!this.airtime) return;
    this.generarChartAirtimeHeatmap(
      this.gatewayMetricasService.procesarAirtimeHeatmap(this.airtime),
    );
  }

  private async traerUplinks(): Promise<void> {
    const end = new Date();
    const start = new Date();
    start.setHours(start.getHours() - 6); // últimas 6 horas

    const startISO = start.toISOString();
    const endISO = end.toISOString();

    try {
      const uplinks = await this.service.getUplinks(
        this.gateway._id,
        startISO,
        endISO,
      );

      if (uplinks && uplinks.length > 0) {
        // Procesar datos para scatter plot
        const resultado = this.gatewayMetricasService.procesarUplinkScatter(
          uplinks,
          this.gateway.gatewayEui,
        );
        // Generar gráfico Highcharts
        this.generarChartScatterUplinks(resultado);
      }
    } catch (error) {
      console.error('Error al traer uplinks:', error);
    }
  }

  // ── Envío de stats (degradación) ──────────────────────────────────────────
  private async traerHistorialStats(): Promise<void> {
    try {
      const end = new Date();
      const start = new Date();
      start.setHours(start.getHours() - 24);

      const res = await this.resumenesService.getFiltered<'Gateway Stats'>({
        filter: JSON.stringify({
          tipo: 'Gateway Stats',
          idsAsignados: [this.gateway._id],
          periodoInicio: { $gte: start.toISOString(), $lte: end.toISOString() },
        }),
        sort: 'periodoInicio',
        limit: 200,
      });

      const datos = (res?.datos ?? []) as IResumenDatosBase<'Gateway Stats'>[];
      this.generarChartHistorialStats(datos);
    } catch (error) {
      console.error('Error al traer historial de stats del gateway:', error);
    }
  }

  private generarChartHistorialStats(
    resumenes: IResumenDatosBase<'Gateway Stats'>[],
  ): void {
    const puntos = resumenes
      .filter((r) => r.periodoInicio && r.resumen)
      .map((r) => {
        const resumen = r.resumen as IResumenGatewayStats;
        return {
          x: new Date(r.periodoInicio!).getTime(),
          y: Math.round((resumen.ratioStats ?? 0) * 100),
          recibidos: resumen.statsRecibidos,
          esperados: resumen.statsEsperados,
        };
      });

    this.chartHistorialStats = {
      chart: { type: 'line', zooming: { type: 'x' } },
      title: { text: undefined },
      xAxis: { type: 'datetime', title: { text: 'Franja (10 min)' } },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: '% de stats recibidos' },
        // Bandas de umbral (ok >= 90, degradado 50-90, crítico < 50)
        plotBands: [
          { from: 0, to: 50, color: 'rgba(211,47,47,0.06)' },
          { from: 50, to: 90, color: 'rgba(255,160,0,0.06)' },
          { from: 90, to: 100, color: 'rgba(76,175,80,0.06)' },
        ],
        plotLines: [
          {
            value: 90,
            color: ChartColors.success(),
            dashStyle: 'Dash',
            width: 1,
            label: { text: 'ok 90%' },
          },
          {
            value: 50,
            color: ChartColors.error(),
            dashStyle: 'Dash',
            width: 1,
            label: { text: 'crítico 50%' },
          },
        ],
      },
      legend: { enabled: false },
      tooltip: {
        pointFormatter(this: Highcharts.Point) {
          const p = this as Highcharts.Point & {
            recibidos?: number;
            esperados?: number;
          };
          const detalle =
            p.recibidos != null && p.esperados != null
              ? `<br/>${p.recibidos} / ${p.esperados} stats`
              : '';
          return `<b>${p.y} %</b>${detalle}`;
        },
      },
      series: [
        {
          type: 'line',
          name: 'Envío de stats',
          data: puntos,
          color: ChartColors.info(),
          marker: { enabled: puntos.length < 60 },
        },
      ],
      credits: { enabled: false },
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metricas']) this.generarGraficos();
    if (changes['airtime']) this.generarGraficoAirtime();
    // Uplinks e historial: una vez por gateway
    if (changes['gateway'] && this.gateway?._id !== this.idPedido) {
      this.idPedido = this.gateway?._id;
      if (this.gateway?._id) {
        this.uplinksCargados = false;
        void this.traerUplinks().finally(() => (this.uplinksCargados = true));
        void this.traerHistorialStats();
      }
    }
  }
}
